/**
 * The way out of a session.
 *
 * Until this existed there was none: the graph was the whole screen and the only route
 * back to the beginning was a browser refresh, which is not an affordance — it is a
 * workaround somebody discovered. So there are two ways out, and they mean different
 * things. **Home** ends the session and returns to the entry screen, where the mode, the
 * intention and the sketchbook live. **New session** stays where you are and clears the
 * path, which is what you want when the last twenty minutes went nowhere and your hands
 * are still on the keys.
 *
 * ## Why the confirm is inline and not a dialog
 *
 * Both discard the working path. It is autosaved — a refresh offers it back — but starting
 * fresh is the one act that deliberately throws it away, so it gets one question. That
 * question is asked *in the header*, in the row the button was in, following the two-tap
 * clear on `/dev/tuning`: a modal over the graph would cover the thing being asked about,
 * and the graph is still live behind everything in this app.
 *
 * The third answer is the interesting one. "Keep it first" saves the progression to the
 * sketchbook and *then* resets, so the honest answer to "are you sure?" — *no, but I still
 * want to start over* — costs one tap rather than a cancel, a save, and a second attempt.
 *
 * Every control here is optional and absent means unwired, the same idiom the rest of
 * `SessionView` uses: under a fixture there is no host to go home to, and a Home button
 * that did nothing would be worse than no Home button.
 */

import { useCallback, useState } from 'react';

/** Which way out was asked for. The confirm needs to know, to phrase itself and to act. */
export type SessionExit = 'home' | 'new';

export interface SessionHeaderProps {
  /**
   * True when there is a progression a reset would discard. With an empty path there is
   * nothing to lose and nothing to ask about, so both actions run on the first tap.
   */
  hasWork: boolean;
  /** End the session and return to the entry screen. */
  onHome?: (() => void) | undefined;
  /** Clear the path and begin again in the mode already chosen. */
  onNewSession?: (() => void) | undefined;
  /** Everything that has been kept. A navigation, so it never needs a confirm. */
  onOpenSketchbook?: (() => void) | undefined;
  /**
   * Keep the progression. Doubles as the header's own Save action and as the "keep it
   * first" branch of the confirm, because they are the same act.
   */
  onSave?: (() => void | Promise<unknown>) | undefined;
}

export function SessionHeader({
  hasWork,
  onHome,
  onNewSession,
  onOpenSketchbook,
  onSave,
}: SessionHeaderProps): React.JSX.Element | null {
  const [pending, setPending] = useState<SessionExit | null>(null);
  const [keeping, setKeeping] = useState(false);

  const run = useCallback(
    (exit: SessionExit) => {
      setPending(null);
      if (exit === 'home') onHome?.();
      else onNewSession?.();
    },
    [onHome, onNewSession],
  );

  const ask = useCallback(
    (exit: SessionExit) => {
      if (hasWork) setPending(exit);
      else run(exit);
    },
    [hasWork, run],
  );

  const keepThenRun = useCallback(
    (exit: SessionExit) => {
      if (!onSave) {
        run(exit);
        return;
      }
      setKeeping(true);
      // Reset whether or not the save succeeded. A storage failure has already been
      // reported by the host and shown as a chip; refusing to start over because the
      // sketchbook is unavailable would trap someone in a session over a second problem.
      void Promise.resolve(onSave()).then(
        () => {
          setKeeping(false);
          run(exit);
        },
        () => {
          setKeeping(false);
          run(exit);
        },
      );
    },
    [onSave, run],
  );

  if (!onHome && !onNewSession && !onOpenSketchbook && !onSave) return null;

  return (
    <header className="hc-session-head" data-testid="hc-session-head">
      {pending ? (
        <div
          className="hc-session-head__confirm"
          data-testid="hc-session-confirm"
          data-exit={pending}
          role="group"
          aria-label={pending === 'home' ? 'Leave this session?' : 'Start over?'}
        >
          <span className="hc-session-head__question">
            {pending === 'home' ? 'Leave this session?' : 'Start over?'}
          </span>
          {onSave ? (
            <button
              type="button"
              className="hc-session-head__action"
              data-testid="hc-confirm-keep"
              disabled={keeping}
              onClick={() => keepThenRun(pending)}
            >
              {keeping ? 'Keeping…' : 'Keep it first'}
            </button>
          ) : null}
          <button
            type="button"
            className="hc-session-head__action hc-session-head__action--commit"
            data-testid="hc-confirm-discard"
            onClick={() => run(pending)}
          >
            {pending === 'home' ? 'Leave' : 'Start fresh'}
          </button>
          <button
            type="button"
            className="hc-session-head__action hc-session-head__action--quiet"
            data-testid="hc-confirm-cancel"
            onClick={() => setPending(null)}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="hc-session-head__row">
          {onHome ? (
            <button
              type="button"
              className="hc-session-head__action"
              data-testid="hc-go-home"
              onClick={() => ask('home')}
            >
              Home
            </button>
          ) : null}
          {onNewSession ? (
            <button
              type="button"
              className="hc-session-head__action"
              data-testid="hc-new-session"
              onClick={() => ask('new')}
            >
              New session
            </button>
          ) : null}
          {onOpenSketchbook ? (
            <button
              type="button"
              className="hc-session-head__action hc-session-head__action--quiet"
              data-testid="hc-session-sketchbook"
              onClick={onOpenSketchbook}
            >
              Sketchbook
            </button>
          ) : null}
          {onSave ? (
            <button
              type="button"
              className="hc-session-head__action"
              data-testid="hc-save"
              onClick={() => void onSave()}
            >
              Save
            </button>
          ) : null}
        </div>
      )}
    </header>
  );
}

export default SessionHeader;
